import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@octano/global-ui';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Container } from 'reactstrap';

import IdentificationTypeLoader from '../../../components/loaders/IdentificationTypeLoader';
import { useParameters } from '../../../hooks/useParameters';
import AgreedPayments from './AgreedPayments';
import Debt from './Debt';
import PaymentHistory from './PaymentHistory';
import StudentCurrentAccountLoader from './components/StudentCurrentAccountLoader';
import StudentSearchAndDetail from './components/StudentSearchAndDetail';

const keyPrefix = 'financialPortal.currentAccount.tabs';

enum TabPaths {
  CurrentAccount = 'current-account',
  PaymentHistoryReceived = 'payment-history-received',
  AgreedPayments = 'agreed-payments',
  PaymentAgreement = 'payment-agreement',
}

interface TabComponentProps {
  currentTab: string;
  onTabChange: (tabId: string | number) => void;
}

const TabComponent = memo(({ currentTab, onTabChange }: TabComponentProps) => {
  const { t } = useTranslation();

  const tabs = [
    {
      path: TabPaths.CurrentAccount,
      name: 'currentAccount',
      component: Debt,
    },
    {
      path: TabPaths.PaymentHistoryReceived,
      name: 'paymentHistoryReceived',
      component: PaymentHistory,
    },
    {
      path: TabPaths.AgreedPayments,
      name: 'agreedPayments',
      component: AgreedPayments,
    },
    // "Acuerdos de pago" tab hidden temporarily — old card-layout view
    // superseded by Pagos Acordados V2 (above). Re-enable when business
    // confirms it's needed alongside V2.
    // {
    //   path: TabPaths.PaymentAgreement,
    //   name: 'paymentAgreement',
    //   component: PaymentAgreement,
    // },
  ];

  return (
    <Container fluid className="p-4">
      <Tabs defaultTab={currentTab} onSelection={onTabChange}>
        <TabsList>
          {tabs.map((tab) => (
            <Tab key={tab.path} id={tab.path}>
              {t(`${keyPrefix}.${tab.name}.title`)}
            </Tab>
          ))}
        </TabsList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.path} id={tab.path}>
              <tab.component />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Container>
  );
});

function StudentCurrentAccount() {
  const [currentTab, setCurrentTab] = useState<string>(TabPaths.CurrentAccount);
  // The search form consumes the identification type list via
  // `useIdentificationTypeLoader`. Scope to the tenant default country so
  // the dropdown matches what the rest of the maintainer flows show.
  const { defaultCountry } = useParameters();

  const handleTabChange = useCallback(
    (tabId: string | number) => {
      if (tabId !== currentTab) {
        setCurrentTab(tabId as string);
      }
    },
    [currentTab],
  );

  return (
    <Card className="mt-5 mx-3">
      <IdentificationTypeLoader countryId={defaultCountry ?? null}>
        <StudentCurrentAccountLoader
          searchComponent={<StudentSearchAndDetail />}
        >
          <TabComponent currentTab={currentTab} onTabChange={handleTabChange} />
        </StudentCurrentAccountLoader>
      </IdentificationTypeLoader>
    </Card>
  );
}

export default StudentCurrentAccount;
